import React, { ComponentProps, useId, useState } from 'react';

import { asFetchableBlock, useFocusOnRefresh, useRefreshBlock } from '../../_lib/BlockFetcher';
import { useDelayed } from '../../hooks/useDelayed';
import { BlockType } from '../../types/api/ElementType';
import { DefaultView, EditView, LazyView, Views } from '../../types/BlockWithViews';
import { useAppContext } from '../../contexts/AppContext/AppContext';
import { useSonTranslation } from '../../translation/useSonTranslation';
import { BlockBar, BlockBarButton } from '../../_lib/components/BlockBar';
import { Icons } from '../../components/atoms/SVG';
import { PageInstantReactionDefaultView } from './views/PageInstantReactionDefaultView';
import { PageInstantReactionEditView } from './views/PageInstantReactionEditView';

type PageInstantReactionProps = Views<
  DefaultView<PageInstantReactionDefaultView> & EditView<PageInstantReactionEditView> & { reactionListView?: LazyView }
>;

export function PageInstantReaction({ views }: PageInstantReactionProps) {
  const { t } = useSonTranslation('PageInstantReaction');
  const { editModeInfo } = useAppContext();
  const { active: editModeActive } = editModeInfo;
  const { refresh, refreshCount } = useRefreshBlock();
  const [editFormVisible, setEditFormVisible] = useState(false);
  const id = useId();
  // refreshcount is passed from blockfetch, increasing when fetch has been triggered.
  const editButtonRef = useFocusOnRefresh<HTMLButtonElement>(refreshCount);

  const onSuccess = useDelayed(() => {
    setEditFormVisible(false);
    refresh();
  });

  return (
    <>
      {editModeActive && (
        <BlockBar heading={t('section.heading')} hierarchy={2}>
          <BlockBarButton
            onClick={() => setEditFormVisible(prev => !prev)}
            ref={editButtonRef}
            text={t('blockEdit.editButtonLabel')}
            svgSpriteId={editFormVisible ? Icons.DECLINE_CROSS : Icons.EDIT_PENCIL}
            aria-controls={id}
            aria-expanded={editFormVisible}
          />
        </BlockBar>
      )}
      {editFormVisible && (
        <div id={id}>
          <PageInstantReactionEditView {...views.edit} onSuccess={onSuccess} />
        </div>
      )}
      {!editModeActive && (
        <PageInstantReactionDefaultView {...views.default} reactionListView={views.reactionListView} />
      )}
    </>
  );
}

export const PageInstantReactionBlock = asFetchableBlock<PageInstantReactionProps, BlockType.pageinstantreaction>(
  PageInstantReaction
);
export type PageInstantReactionBlock = ComponentProps<typeof PageInstantReactionBlock>;
